/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { useCssVars } from '#hooks/PRIVATE_useCssVars';
import { baseVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { xUnits } from '#UNSAFE_Theme/utils';
import { pxToNumber } from '#PRIVATE_TimeComponent/utils/timeComponentStyleUtils';
import { bridgeValues } from '../themes/GanttStyles.css';

const ganttUnresolvedVars: Parameters<typeof useCssVars>[0] = [
  {
    tokenVar: bridgeValues.rowLabelPaddingInlineStart,
    key: 'rowLabelPaddingInlineStart',
    cssProp: 'padding-inline-start'
  },
  {
    tokenVar: bridgeValues.rowLabelPaddingInlineStart,
    key: 'rowLabelPaddingInlineEnd',
    cssProp: 'padding-inline-end'
  },
  {
    tokenVar: xUnits(3),
    key: 'rowPaddingTop',
    cssProp: 'padding-top'
  },
  {
    tokenVar: xUnits(3),
    key: 'rowPaddingBottom',
    cssProp: 'padding-bottom'
  },
  {
    tokenVar: xUnits(6),
    key: 'taskHeight',
    cssProp: 'min-height'
  },
  {
    tokenVar: xUnits(1),
    key: 'taskBorderRadius',
    cssProp: 'border-radius'
  },
  {
    tokenVar: bridgeValues.taskBgColor,
    key: 'taskBgColor',
    cssProp: 'background-color'
  },
  {
    tokenVar: bridgeValues.taskBorderColor,
    key: 'taskBorderColor',
    cssProp: 'border-color'
  },
  {
    tokenVar: bridgeValues.taskEffectBorderColor,
    key: 'taskEffectBorderColor',
    cssProp: 'outline-color'
  },
  {
    tokenVar: baseVars.boxShadow.md,
    key: 'taskEffectBoxShadow',
    cssProp: 'box-shadow'
  }
];

/**
 * Parses a CSS box-shadow string.
 * @param boxShadow A CSS box-shadow string, of the form "color offsetX offsetY blur spread".
 * @returns The components of the box-shadow.
 */
const parseBoxShadow = (boxShadow: string) => {
  // boxShadow: "color offsetX offsetY blur spread"
  // color may have spaces e.g. rgb(0, 0, 0, 0.2)
  const [_, shadowBlur, shadowOffsetY, shadowOffsetX, ...rest] = boxShadow.split(' ').reverse();
  const shadowColor = rest.reverse().join(' ');
  return {
    shadowColor,
    shadowOffsetX: pxToNumber(shadowOffsetX ?? '0'),
    shadowOffsetY: pxToNumber(shadowOffsetY ?? '0'),
    shadowBlur: pxToNumber(shadowBlur ?? '0')
  };
};

/**
 * Gets the resolved row axis related CSS vars.
 * @param resolvedVars The resolved CSS vars.
 * @returns The resolved values.
 */
const getResolvedRowAxisVars = (resolvedVars: ReturnType<typeof useCssVars>['resolvedVars']) => ({
  rowLabelPaddingInlineStart: pxToNumber(resolvedVars['rowLabelPaddingInlineStart']),
  rowLabelPaddingInlineEnd: pxToNumber(resolvedVars['rowLabelPaddingInlineEnd'])
});

/**
 * Gets the resolved row related CSS vars.
 * @param resolvedVars The resolved CSS vars.
 * @returns The resolved values.
 */
const getResolvedRowVars = (resolvedVars: ReturnType<typeof useCssVars>['resolvedVars']) => ({
  rowPaddingTop: pxToNumber(resolvedVars['rowPaddingTop']),
  rowPaddingBottom: pxToNumber(resolvedVars['rowPaddingBottom'])
});

/**
 * Gets the resolved task related CSS vars.
 * @param resolvedVars The resolved CSS vars.
 * @returns The resolved values.
 */
const getResolvedTaskVars = (resolvedVars: ReturnType<typeof useCssVars>['resolvedVars']) => ({
  taskHeight: pxToNumber(resolvedVars['taskHeight']),
  taskBorderRadius: resolvedVars['taskBorderRadius'] as string,
  taskBgColor: resolvedVars['taskBgColor'] as string,
  taskBorderColor: resolvedVars['taskBorderColor'] as string,
  taskEffectBorderColor: resolvedVars['taskEffectBorderColor'] as string,
  taskEffectBoxShadow: parseBoxShadow(resolvedVars['taskEffectBoxShadow'])
});

export { ganttUnresolvedVars, getResolvedRowAxisVars, getResolvedRowVars, getResolvedTaskVars };
